import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LogoFull } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { ARTICLES } from "@/data/articles";
import { ArrowLeft, ArrowRight, Clock, User, Calendar, Share2 } from "lucide-react";

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] },
});

export default function BlogArticlePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const article = ARTICLES.find(a => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center" style={{ background: "var(--lb-bg)" }}>
        <p className="text-lg font-semibold mb-4" style={{ color: "var(--lb-text)" }}>Article not found</p>
        <Button onClick={() => navigate("/blog")} className="lb-gradient-btn">Back to Blog</Button>
      </div>
    );
  }

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: article.title, text: article.excerpt, url }); } catch {}
    } else {
      navigator.clipboard.writeText(url);
    }
  };

  const otherArticles = ARTICLES.filter(a => a.slug !== slug).slice(0, 3);

  return (
    <div className="min-h-screen" style={{ background: "var(--lb-bg)" }}>
      <nav className="flex items-center justify-between px-6 md:px-12 py-5 max-w-7xl mx-auto">
        <LogoFull iconSize={34} className="cursor-pointer" onClick={() => navigate("/")} />
        <Button onClick={() => navigate("/")} className="lb-gradient-btn !px-6 !py-2 text-sm">Get Started</Button>
      </nav>

      {/* Hero banner */}
      <motion.div {...fadeUp()} className="mx-6 md:mx-12 lg:mx-auto max-w-3xl rounded-2xl overflow-hidden relative"
        style={{ background: `linear-gradient(135deg, ${article.color}dd, ${article.color}88)` }}>
        <div className="p-8 md:p-12">
          <button onClick={() => navigate("/blog")} className="flex items-center gap-1.5 text-white/60 text-sm mb-6 hover:text-white/90 transition-colors">
            <ArrowLeft className="w-4 h-4" /> All articles
          </button>
          <span className="text-xs font-semibold tracking-wider uppercase px-3 py-1 rounded-full text-white/80"
            style={{ background: "rgba(255,255,255,0.15)" }}>{article.category}</span>
          <h1 className="text-2xl md:text-4xl font-semibold text-white mt-4 mb-4 leading-tight" style={{ fontFamily: "Outfit" }}>
            {article.title}
          </h1>
          <div className="flex flex-wrap items-center gap-4 text-xs text-white/55">
            <span className="flex items-center gap-1"><User className="w-3 h-3" /> {article.author}</span>
            <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {article.date}</span>
            <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {article.readTime} read</span>
          </div>
        </div>
      </motion.div>

      {/* Article body */}
      <article className="px-6 md:px-12 lg:px-0 max-w-3xl mx-auto py-10">
        {article.content.map((block, i) => {
          if (block.type === "h2") {
            return (
              <motion.h2 key={i} {...fadeUp(i * 0.02)}
                className="text-xl md:text-2xl font-semibold mt-10 mb-4"
                style={{ fontFamily: "Outfit", color: "var(--lb-text)" }}>
                {block.text}
              </motion.h2>
            );
          }
          if (block.type === "p") {
            return (
              <motion.p key={i} {...fadeUp(i * 0.02)}
                className="text-base leading-[1.85] mb-5"
                style={{ color: "var(--lb-text-muted)" }}>
                {block.text}
              </motion.p>
            );
          }
          if (block.type === "list") {
            return (
              <motion.ul key={i} {...fadeUp(i * 0.02)} className="space-y-3 mb-6 pl-1">
                {block.items.map((item, j) => (
                  <li key={j} className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full mt-2.5 shrink-0" style={{ background: article.color }} />
                    <span className="text-base leading-[1.8]" style={{ color: "var(--lb-text-muted)" }}>{item}</span>
                  </li>
                ))}
              </motion.ul>
            );
          }
          return null;
        })}

        {/* Share + CTA */}
        <div className="mt-12 pt-8 flex flex-col sm:flex-row items-center gap-4" style={{ borderTop: "1px solid var(--lb-border)" }}>
          <button onClick={handleShare}
            className="flex items-center gap-2 px-6 py-2.5 rounded-full border-2 text-sm font-semibold transition-all hover:shadow-md"
            style={{ borderColor: "var(--lb-border)", color: "var(--lb-text)" }}>
            <Share2 className="w-4 h-4" /> Share this article
          </button>
          <Button onClick={() => navigate("/")} className="lb-gradient-btn !px-8">
            Try LiveBetter Free <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </div>
      </article>

      {/* Related articles */}
      <section className="px-6 md:px-12 lg:px-0 max-w-3xl mx-auto pb-16">
        <h3 className="text-lg font-semibold mb-6" style={{ fontFamily: "Outfit", color: "var(--lb-text)" }}>Keep reading</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {otherArticles.map((a, i) => (
            <motion.div key={i} {...fadeUp(i * 0.06)}
              onClick={() => navigate(`/blog/${a.slug}`)}
              className="lb-card-interactive !p-0 overflow-hidden cursor-pointer">
              <div className="h-1.5 w-full" style={{ background: a.color }} />
              <div className="p-4">
                <span className="text-[10px] font-semibold tracking-wider uppercase px-2 py-0.5 rounded-full"
                  style={{ background: `${a.color}12`, color: a.color }}>{a.category}</span>
                <h4 className="text-sm font-semibold mt-2 leading-snug"
                  style={{ fontFamily: "Outfit", color: "var(--lb-text)" }}>{a.title}</h4>
                <p className="text-xs mt-1" style={{ color: "var(--lb-text-muted)" }}>{a.readTime} read</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      <footer className="px-6 md:px-12 py-8 max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
        <LogoFull iconSize={24} />
        <p className="text-xs" style={{ color: "var(--lb-text-muted)" }}>LiveBetter is a wellness companion, not a medical device.</p>
      </footer>
    </div>
  );
}
